import { Box, ButtonBase, List, ListItem, Typography, makeStyles } from "@material-ui/core";
import { alpha } from "@material-ui/core/styles";
import CheckIcon from "@material-ui/icons/Check";
import clsx from "clsx";
import * as React from "react";

import { CustomButton } from "@/components/Button";
import { Chevron } from "@/components/Chevron";
import { ProgressBar } from "@/components/ProgressBar";
import { LEARNING_STAGES, LearningArticleDefinition, LearningStageDefinition } from "@/constants/learning";
import { fontFamilies, layoutTokens } from "@/designTokens";
import theme from "@/theme";
import { LearningArticleStatusMap } from "@/view_models/atoms";

const color = theme.palette.semantic;
export const useLearningLayout = makeStyles(() => ({
  page: {
    padding: theme.spacing(3),
    paddingBottom: theme.spacing(4),
    [theme.breakpoints.up("md")]: { padding: theme.spacing(4, 0) },
  },
  head: { display: "flex", flexDirection: "column", gap: theme.spacing(1.5), marginBottom: theme.spacing(3) },
  title: { fontSize: 20, lineHeight: 1.3, fontWeight: 700, [theme.breakpoints.up("md")]: { fontSize: 24 } },
  description: { fontSize: 12, color: theme.palette.text.secondary, [theme.breakpoints.up("md")]: { fontSize: 14 } },
  back: {
    alignSelf: "flex-start",
    color: color["action/primary"],
    fontSize: 14,
    minHeight: 40,
    lineHeight: 1.3,
    padding: theme.spacing(1.5, 0),
    margin: theme.spacing(-1.5, 0),
    justifyContent: "flex-start",
    gap: theme.spacing(0.5),
  },
  columns: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr)",
    gap: theme.spacing(3),
    [theme.breakpoints.up("md")]: { gridTemplateColumns: "minmax(0, 384fr) minmax(0, 776fr)", gap: theme.spacing(5) },
  },
  cards: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(3),
    [theme.breakpoints.up("md")]: { gap: theme.spacing(2) },
  },
  main: { minWidth: 0 },
  state: {
    padding: theme.spacing(3),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    alignItems: "center",
    textAlign: "center",
    borderRadius: layoutTokens.radius.lg,
    backgroundColor: color["surface/card"],
  },
}));
const useStyles = makeStyles(() => ({
  card: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    textAlign: "left",
    padding: theme.spacing(2),
    gap: theme.spacing(1.5),
    borderRadius: layoutTokens.radius.md,
    backgroundColor: color["surface/base"],
    [theme.breakpoints.up("md")]: {
      borderRadius: layoutTokens.radius.lg,
      backgroundColor: color["surface/card"],
      boxShadow: `0 2px 8px ${alpha(color["text/primary"], 0.06)}`,
    },
    "&:focus-visible": { outline: `2px solid ${color["action/primary"]}`, outlineOffset: 2 },
  },
  selected: { [theme.breakpoints.up("md")]: { backgroundColor: color["state/selected-bg"] } },
  cardTop: { display: "flex", alignItems: "center", gap: theme.spacing(1) },
  texts: { flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: theme.spacing(0.5) },
  label: { fontSize: 12, fontWeight: 700, lineHeight: 1.3, color: theme.palette.text.secondary },
  cardTitle: { fontSize: 16, fontWeight: 700, lineHeight: 1.3 },
  message: { fontSize: 12, lineHeight: 1.5, color: theme.palette.text.secondary, whiteSpace: "pre-line" },
  progress: { display: "flex", flexDirection: "column", gap: theme.spacing(1) },
  progressText: {
    display: "flex",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    fontSize: 12,
    color: theme.palette.text.secondary,
  },
  continue: { color: color["action/primary"] },
  content: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(3),
    minWidth: 0,
    [theme.breakpoints.up("md")]: {
      padding: theme.spacing(4),
      borderRadius: layoutTokens.radius.lg,
      backgroundColor: color["surface/card"],
    },
  },
  compact: {
    [theme.breakpoints.up("md")]: { padding: theme.spacing(3), gap: theme.spacing(2), position: "sticky", top: 88 },
  },
  header: { display: "flex", flexDirection: "column", gap: theme.spacing(1) },
  stageTitle: { fontSize: 20, fontWeight: 700, lineHeight: 1.3, [theme.breakpoints.up("md")]: { fontSize: 16 } },
  objective: { fontSize: 14, lineHeight: 1.5, [theme.breakpoints.up("md")]: { fontSize: 16 } },
  completeObjective: { display: "none", [theme.breakpoints.up("md")]: { display: "block" } },
  sectionTitle: {
    fontSize: 14,
    fontWeight: 700,
    lineHeight: 1.3,
    marginBottom: theme.spacing(1),
    [theme.breakpoints.up("md")]: { fontSize: 16 },
  },
  row: {
    display: "flex",
    gap: theme.spacing(1.5),
    padding: "14px 0",
    borderBottom: `1px solid ${color["border/default"]}`,
    [theme.breakpoints.up("md")]: { padding: theme.spacing(2, 0) },
  },
  completed: { opacity: 0.75, color: theme.palette.text.secondary },
  current: {
    backgroundColor: color["state/selected-bg"],
    borderRadius: layoutTokens.radius.md,
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
  },
  num: {
    width: 24,
    height: 24,
    flexShrink: 0,
    borderRadius: layoutTokens.radius.full,
    border: `1.5px solid ${color["border/default"]}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 14,
    fontFamily: fontFamilies.number,
    fontWeight: 600,
    lineHeight: 1,
    color: theme.palette.text.secondary,
  },
  check: {
    backgroundColor: color["status/success"],
    borderColor: color["status/success"],
    color: color["text/inverse"],
  },
  rowTitle: { fontSize: 14, fontWeight: 500, lineHeight: 1.3, flex: 1, minWidth: 0, overflowWrap: "anywhere" },
  completeBand: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    padding: theme.spacing(3),
    backgroundColor: color["surface/base"],
    margin: theme.spacing(0, -3),
    [theme.breakpoints.up("md")]: { padding: 0, margin: 0, backgroundColor: "transparent" },
  },
  completeMessage: {
    padding: theme.spacing(3, 2),
    textAlign: "center",
    borderRadius: layoutTokens.radius.lg,
    backgroundColor: color["surface/card"],
    [theme.breakpoints.up("md")]: { backgroundColor: color["surface/base"] },
  },
  completeTitle: { fontSize: 16, fontWeight: 700, marginBottom: theme.spacing(1) },
  completeText: { fontSize: 14, color: theme.palette.text.secondary },
  next: {
    display: "flex",
    textAlign: "left",
    width: "100%",
    gap: theme.spacing(2),
    padding: theme.spacing(2),
    borderRadius: layoutTokens.radius.lg,
    backgroundColor: color["surface/card"],
    boxShadow: `0 2px 8px ${alpha(color["text/primary"], 0.06)}`,
  },
}));
export interface LearningStageCardProps {
  label: string;
  title: string;
  message: string;
  selected?: boolean;
  total?: number;
  completed?: number;
  onClick: () => void;
}
export const LearningStageCard: React.FC<LearningStageCardProps> = ({
  label,
  title,
  message,
  selected,
  total = 0,
  completed = 0,
  onClick,
}) => {
  const classes = useStyles();
  return (
    <ButtonBase
      role="button"
      className={clsx(classes.card, selected && classes.selected)}
      onClick={onClick}
      aria-pressed={selected}
    >
      <Box className={classes.cardTop}>
        <Box className={classes.texts}>
          <Typography className={classes.label}>{label}</Typography>
          <Typography className={classes.cardTitle}>{title}</Typography>
          <Typography className={classes.message}>{message}</Typography>
        </Box>
        <Chevron />
      </Box>
      {total > 0 && (
        <Box className={classes.progress}>
          <Box className={classes.progressText}>
            <span>
              {total}章中 {completed}章
            </span>
            {completed > 0 && completed < total && <span className={classes.continue}>続きから読む</span>}
          </Box>
          <ProgressBar value={(completed / total) * 100} aria-label={`${label}の進み具合`} />
        </Box>
      )}
    </ButtonBase>
  );
};
export interface LearningStageContentProps {
  stage: LearningStageDefinition;
  statusMap: LearningArticleStatusMap;
  processingArticleId?: string | null;
  onOpenArticle: (article: LearningArticleDefinition) => void;
  onNextStage?: (stage: LearningStageDefinition) => void;
  currentArticleId?: string;
  compact?: boolean;
}
export const LearningStageContent: React.FC<LearningStageContentProps> = ({
  stage,
  statusMap,
  processingArticleId,
  onOpenArticle,
  onNextStage,
  currentArticleId,
  compact,
}) => {
  const classes = useStyles();
  const completed = stage.articles.filter((article) => statusMap[article.id]).length;
  const allCompleted = stage.articles.length > 0 && completed === stage.articles.length;
  const next = LEARNING_STAGES[LEARNING_STAGES.findIndex((item) => item.id === stage.id) + 1];
  return (
    <Box className={clsx(classes.content, compact && classes.compact)}>
      <Box className={classes.header}>
        <Typography className={classes.label}>{stage.label}</Typography>
        <Typography component="h2" className={classes.stageTitle}>
          {stage.title}
        </Typography>
        {!compact && (
          <Typography className={clsx(classes.objective, allCompleted && classes.completeObjective)}>
            {stage.objective}
          </Typography>
        )}
      </Box>
      {allCompleted && !compact ? (
        <Box className={classes.completeBand}>
          <Box className={classes.completeMessage}>
            <Typography className={classes.completeTitle}>{stage.label}を読み終えました</Typography>
            <Typography className={classes.completeText}>
              {stage.articles.length}章すべて読みました。
              {next ? `次は、${next.message.replace(/\n/g, "")}` : `${stage.label}の学習を終えました。`}
            </Typography>
          </Box>
          {next && onNextStage && (
            <ButtonBase className={classes.next} onClick={() => onNextStage(next)}>
              <Box className={classes.texts}>
                <Typography className={classes.message}>次の段階</Typography>
                <Typography className={classes.rowTitle}>
                  {next.label}：{next.title}（全{next.articles.length}章）
                </Typography>
              </Box>
              <Chevron />
            </ButtonBase>
          )}
        </Box>
      ) : (
        <Box className={classes.progress}>
          <Typography className={classes.progressText}>
            {stage.articles.length}章中 {completed}章を読みました
          </Typography>
          <ProgressBar
            value={stage.articles.length ? (completed / stage.articles.length) * 100 : 0}
            aria-label={`${stage.label}の進み具合`}
          />
        </Box>
      )}
      <Box>
        <Typography component="h3" className={classes.sectionTitle}>
          {allCompleted && !compact ? "読み終えた章" : "このレベルの章"}
        </Typography>
        <List disablePadding>
          {stage.articles.map((article, index) => {
            const isCompleted = Boolean(statusMap[article.id]);
            return (
              <ListItem
                key={article.id}
                button
                component="button"
                type="button"
                className={clsx(
                  classes.row,
                  isCompleted && classes.completed,
                  article.id === currentArticleId && classes.current
                )}
                aria-current={article.id === currentArticleId ? "page" : undefined}
                aria-label={`${article.title}${isCompleted ? "（読了済み）" : ""}`}
                data-completed={isCompleted}
                disabled={processingArticleId === article.id}
                onClick={() => onOpenArticle(article)}
              >
                <span className={clsx(classes.num, isCompleted && classes.check)}>
                  {isCompleted ? <CheckIcon style={{ fontSize: 12 }} /> : index + 1}
                </span>
                <Typography className={classes.rowTitle}>{article.title}</Typography>
                <Chevron />
              </ListItem>
            );
          })}
        </List>
      </Box>
    </Box>
  );
};
export const LearningFailure: React.FC<{ error: string; onRetry: () => void }> = ({ error, onRetry }) => {
  const classes = useLearningLayout();
  return (
    <Box role="alert" className={classes.state}>
      <Typography>{error}</Typography>
      <CustomButton tone="outline" buttonSize="M" onClick={onRetry}>
        再読み込み
      </CustomButton>
    </Box>
  );
};
